import React, { useEffect, useRef, useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Organisation } from "../types";
import { Building, CheckCircle, ChevronDown, Plus, User } from "./Icons";

interface OrgSwitcherProps {
  currentOrg: Organisation;
  allOrgs: Organisation[];
  onChange: (org: Organisation) => void;
  onOrgCreated: (org: Organisation) => void;
  onOpenCreateOrg: () => void;
}

const OrgSwitcher: React.FC<OrgSwitcherProps> = ({
  currentOrg,
  allOrgs,
  onChange,
  onOrgCreated,
  onOpenCreateOrg,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Group Orgs
  const isPersonal = (o: Organisation) => o.type === "PERSONAL";

  const personal = allOrgs.filter(isPersonal);
  const shared = allOrgs.filter((o) => !isPersonal(o));

  const isCurrentPersonal = isPersonal(currentOrg);

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="org-switcher-trigger"
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-800/50 transition-colors text-left group border border-transparent hover:border-slate-700"
      >
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center text-white shadow-lg ${isCurrentPersonal ? "bg-gradient-to-br from-emerald-500 to-emerald-700 shadow-emerald-500/20" : "bg-gradient-to-br from-indigo-500 to-indigo-700 shadow-indigo-500/20"}`}
        >
          {isCurrentPersonal ? (
            <User size={16} className="text-white/90" />
          ) : (
            <Building size={16} className="text-white/90" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium text-slate-400 group-hover:text-slate-300">
            Workspace
          </div>
          <div className="text-sm font-bold text-slate-200 truncate">
            {currentOrg.name}
          </div>
        </div>
        <ChevronDown
          size={14}
          className={`text-slate-500 group-hover:text-slate-300 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-full bg-slate-900 rounded-xl border border-slate-700/50 shadow-xl shadow-black/50 z-dropdown overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <div
            data-testid="org-switcher-options"
            className="max-h-[60vh] overflow-y-auto"
          >
            {personal.length > 0 && (
              <div className="mx-2 py-2">
                <div className="px-4 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Personal
                </div>
                {personal.map((org) => (
                  <OrgItem
                    key={org.id}
                    org={org}
                    isPersonal={true}
                    active={currentOrg.id === org.id}
                    onClick={() => {
                      onChange(org);
                      setIsOpen(false);
                    }}
                  />
                ))}
              </div>
            )}

            {shared.length > 0 && (
              <div className="border-t border-slate-800">
                <div className="mx-2 py-2">
                  <div className="px-4 py-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Shared
                  </div>
                  {shared.map((org) => (
                    <OrgItem
                      key={org.id}
                      org={org}
                      isPersonal={false}
                      active={currentOrg.id === org.id}
                      onClick={() => {
                        onChange(org);
                        setIsOpen(false);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-2 border-t border-slate-800 bg-slate-900/50 backdrop-blur-sm">
            <button
              onClick={() => {
                onOpenCreateOrg();
                setIsOpen(false);
              }}
              data-testid="create-org-button"
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-indigo-400 hover:bg-indigo-500/10 hover:text-indigo-300 transition-colors group/btn"
            >
              <Plus size={14} /> Create shared workspace
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const OrgItem: React.FC<{
  org: Organisation;
  isPersonal: boolean;
  active: boolean;
  onClick: () => void;
}> = ({ org, isPersonal, active, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors group ${active ? "bg-indigo-600 text-white shadow-md shadow-indigo-900/20" : "text-slate-400 hover:text-slate-200 hover:bg-slate-800"}`}
  >
    <div className="flex items-center gap-3 min-w-0">
      {active ? (
        <div className="w-6 h-6 bg-white/20 rounded-md flex items-center justify-center">
          {isPersonal ? (
            <User size={12} className="text-white" />
          ) : (
            <Building size={12} className="text-white" />
          )}
        </div>
      ) : (
        <div className="w-6 h-6 bg-slate-800 rounded-md flex items-center justify-center text-slate-500 group-hover:text-slate-300 group-hover:bg-slate-700 transition-colors">
          {isPersonal ? <User size={12} /> : <Building size={12} />}
        </div>
      )}
      <span className="font-medium truncate">{org.name}</span>
    </div>
    {active && <CheckCircle size={14} className="text-white/80" />}
  </button>
);

export default OrgSwitcher;
